import { useContext, useEffect, useState } from "react";
import translations from "../../../translations/translations";
import "./AdminServiceCategoriesModal.css";
import { CombinedContext } from "../../../context/combinedContext";
import companyBasedRequest from "../../../request/companyBasedRequest";
import BannerMessage from "../../common/bannerMessage/bannerMessage";
import { Trash2 } from "lucide-react";

const AdminServiceCategoriesModal = ({
	isOpen,
	onClose,
	shopId,
	initialCategories = [],
	language = "en",
	onSave,
}) => {
	const { userData, setUserData} = useContext(CombinedContext);

	const [categories, setCategories] = useState([]);
	const [saving, setSaving] = useState(false);
	const [banner, setBanner] = useState(null);
	const [exiting, setExiting] = useState(false);

 
	const showBanner = (type, text, duration = 2000) => {
		setBanner({ type, text });
		setExiting(false);

		setTimeout(() => {
			setExiting(true);
		}, duration - 250);

		setTimeout(() => {
			setBanner(null);
			setExiting(false);
		}, duration);
	};

	useEffect(() => {
		if (!isOpen) return;
		// deep copy so we don't mutate parent state
		setCategories(JSON.parse(JSON.stringify(initialCategories)));
	}, [isOpen, initialCategories]);

	if (!isOpen) return null;

	const updateName = (id, value) => {
		setCategories((prev) =>
			prev.map((c) =>
				c.categoryId === id
					? {
							...c,
							name: {
								en: value,
								es: value,
							},
					  }
					: c
			)
		);
	};

	const addCategory = () => {
		setCategories((prev) => {
			const nextOrder =
				Math.max(0, ...prev.map((c) => Number(c.order) || 0)) + 1;
	
			return [
				...prev,
				{
					categoryId: `cat_${crypto.randomUUID()}`,
					name: {
						en: "New category",
						es: "Nueva categoría",
					},
					order: nextOrder,
				},
			];
		});
	};

	const deleteCategory = (categoryId) => {
		setCategories((prev) => prev.filter((c) => c.categoryId !== categoryId));
	};

	const handleSave = async () => {
		if (!shopId) {
			showBanner("error", "Missing shopId");
			return;
		}

		try {
			setSaving(true);

			// Optional: normalize order
			const normalized = categories.map((c, idx) => ({
				...c,
				order: idx + 1,
			}));

			const token = userData?.cognitoTokens?.AccessToken;

			const res = await companyBasedRequest.updateShopServiceCategories(
				shopId,
				normalized,
				token
			);

			// Depending on your API response, pick one:
			// - if res.shop exists:
			// onSave(res.shop);
			// - if res.serviceCategories exists:
			// onSave(res.serviceCategories);
			// We'll support both safely:
			const updatedCategories =
				res?.shop?.serviceCategories ||
				res?.serviceCategories ||
				normalized;

			onSave?.(updatedCategories);
			setUserData((prev) => ({
                ...prev,
                shop: {
                    ...prev.shop,
                    serviceCategories: res.serviceCategories,
                },
            }));

			onClose();
		} catch (err) {
			console.error("Failed to save categories:", err);
			showBanner(
				"error",
				translations[language].calendar.editor?.errorSave || "Failed to save changes"
			);
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="sf-cat-overlay" onClick={onClose}>
			<div className="sf-cat-modal" onClick={(e) => e.stopPropagation()}>
				{/* Header */}
				<div className="sf-cat-header">
					<h3>
						{language === "es"
							? "Categorías de servicios"
							: "Service categories"}
					</h3>
					<button onClick={onClose} disabled={saving}>
						✕
					</button>
				</div>

				{/* Body */}
				<div className="sf-cat-body">
					{categories.map((cat) => (
						<div key={cat.categoryId} className="sf-cat-row">
							<input
								className="sf-cat-input"
								value={
									typeof cat.name === "string"
										? cat.name
										: cat.name?.[language] || cat.name?.en || ""
								}
								onChange={(e) =>
									updateName(cat.categoryId, e.target.value)
								}
								disabled={saving}
							/>

							{!cat.isDefault && (
								<button
									className="sf-adminsv-iconbtn sf-adminsv-remove"
									onClick={() => deleteCategory(cat.categoryId)}
									disabled={saving}
									aria-label="Delete category"
								>
									<Trash2 size={20} strokeWidth={1.8} />
								</button>
							)}
						</div>
					))}

					<button
						className="sf-cat-add"
						onClick={addCategory}
						disabled={saving}
					>
						＋ {language === "es" ? "Añadir categoría" : "Add category"}
					</button>
				</div>

				{/* Footer */}
				{/* <div className="sf-cat-footer">
					<button className="sf-btn-secondary" onClick={onClose} disabled={saving}>
						{translations[language].profile.actions.cancel}
					</button>

					<button className="sf-btn-primary" onClick={handleSave} disabled={saving}>
						{saving
							? language === "es"
								? "Guardando..."
								: "Saving..."
							: translations[language].profile.actions.saveChanges}
					</button>
				</div> */}
				 <div className="sf-base-footer">
					<button className="btn ghost" onClick={onClose}>
						{translations[language].calendar.editor.cancel}
					</button>
					<button className="btn" onClick={handleSave} disabled={saving}>
						{saving ? translations[language].calendar.editor.saving : translations[language].calendar.editor.save}
					</button>
				</div>
			</div>
			{banner && (
				<BannerMessage
					type={banner.type}
					text={banner.text}
					exiting={exiting}
				/>
			)}
		</div>
	);
};

export default AdminServiceCategoriesModal;
